import { defineStore } from 'pinia'

// A simple hash function for the product ID to get a consistent seed value
function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash |= 0 // Convert to 32bit integer
  }
  return hash
}

// Seeded random number generator function
function seededRandom(seed: number): () => number {
  return function (): number {
    const x = Math.sin(seed++) * 10000
    return x - Math.floor(x)
  }
}

function _complexLogic(base: number, factor: number, randomFunc: () => number): number {
  return Math.round(base * randomFunc() * factor)
}

interface ProductMetrics {
  soldCount: number
  soldCountLast24h: number
  repeatPurchaseCount: number
  currentlyViewingCount: number
}

export const useProductMetricsStore = defineStore('productMetrics', () => {
  const metricsCache = useState<Record<string, ProductMetrics>>(() => ({}))

  // Fixed timestamp values for consistent results
  const timestamp = Date.now()
  const hourOfDay = new Date(timestamp).getHours()
  const daySeed = Math.floor(timestamp / (1000 * 60 * 60 * 24))
  const minuteSeed = Math.floor(timestamp / (1000 * 60 * 5))

  // Get metrics for a product
  function getMetricsForProduct(productId: string, reviewCount = 0): ProductMetrics {
    const cacheKey = `${productId}-${reviewCount}-${daySeed}`
    if (metricsCache.value[cacheKey]) {
      return metricsCache.value[cacheKey]
    }

    const productHash = hashString(productId) + daySeed
    const randomFunc = seededRandom(productHash)

    let totalSoldCount: number
    let soldCountLast24h: number

    // Peak hours determination
    const isPeakHour = (hourOfDay >= 9 && hourOfDay < 12) || (hourOfDay >= 18 && hourOfDay < 21)
    const peakMultiplier = isPeakHour ? 1.3 : 1.0

    // Gradual progression based on review count
    let minSales24h: number
    let maxSales24h: number
    let salesMultiplier: number // How many times reviews translate to sales

    if (reviewCount === 0) {
      // New products with no reviews: 10-49 sales/24h
      minSales24h = 10
      maxSales24h = 49
      salesMultiplier = 50 + randomFunc() * 150 // 50-200x (no reviews, so we estimate total sales)
    }
    else if (reviewCount <= 5) {
      // Products with few reviews: 20-69 sales/24h
      minSales24h = 20
      maxSales24h = 69
      salesMultiplier = 80 + randomFunc() * 40 // 80-120x reviews
    }
    else if (reviewCount <= 20) {
      // Products with moderate reviews: 50-99 sales/24h
      minSales24h = 50
      maxSales24h = 99
      salesMultiplier = 60 + randomFunc() * 30 // 60-90x reviews
    }
    else if (reviewCount <= 50) {
      // Products with good reviews: 80-149 sales/24h
      minSales24h = 80
      maxSales24h = 149
      salesMultiplier = 40 + randomFunc() * 20 // 40-60x reviews
    }
    else {
      // Popular products with many reviews: 100-199 sales/24h
      minSales24h = 100
      maxSales24h = 199
      salesMultiplier = 25 + randomFunc() * 15 // 25-40x reviews
    }

    // Calculate sales for last 24h with variation
    const baseCount = minSales24h + randomFunc() * (maxSales24h - minSales24h)
    soldCountLast24h = Math.round(baseCount * peakMultiplier)
    soldCountLast24h = Math.max(minSales24h, Math.min(maxSales24h, soldCountLast24h))

    // Calculate total sales
    if (reviewCount === 0) {
      // For products with no reviews, use the multiplier on daily sales
      totalSoldCount = Math.round(soldCountLast24h * salesMultiplier)
    }
    else {
      // For products with reviews, multiply reviews by the sales multiplier
      totalSoldCount = Math.round(reviewCount * salesMultiplier)
    }

    // Calculate repeat purchases (5-10% of total)
    const repeatPercentage = 0.05 + randomFunc() * 0.05
    const repeatPurchaseCount = Math.round(totalSoldCount * repeatPercentage)

    // Calculate currently viewing based on sales activity
    let minViewing: number
    let maxViewing: number

    if (reviewCount === 0) {
      minViewing = 3
      maxViewing = 15
    }
    else if (reviewCount <= 5) {
      minViewing = 5
      maxViewing = 20
    }
    else if (reviewCount <= 20) {
      minViewing = 10
      maxViewing = 35
    }
    else if (reviewCount <= 50) {
      minViewing = 15
      maxViewing = 50
    }
    else {
      minViewing = 20
      maxViewing = 80
    }

    const baseViewing = minViewing + randomFunc() * (maxViewing - minViewing)
    const viewingVariation = seededRandom(productHash + minuteSeed)() * 0.3 + 0.85
    const currentlyViewingCount = Math.round(baseViewing * peakMultiplier * viewingVariation)
    const finalViewingCount = Math.max(minViewing, Math.min(maxViewing, currentlyViewingCount))

    // Create and cache the metrics
    const metrics = {
      soldCount: totalSoldCount,
      soldCountLast24h,
      repeatPurchaseCount,
      currentlyViewingCount: finalViewingCount,
    }
    metricsCache.value[cacheKey] = metrics

    return metrics
  }

  return {
    metricsCache,
    getMetricsForProduct,
  }
})
